// Real: Products Listing (for Products page)
// src/api.js
// Centralized API service for all backend calls
import { API_ENDPOINTS } from './apiEndpoints';

// Simulate network delay for demo data
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const api = {
  // Real login call
  login: async (email, password) => {
    const response = await fetch(API_ENDPOINTS.adminLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },

  // Demo: Dashboard stats
  getDashboardStats: async () => {
    await delay(500);
    return {
      dailySignups: 25,
      dailyVisitors: 300,
      dailyOrders: 120,
      dailyRevenue: 4500,
    };
  },

  // Metrics: Users count
  getUserMetrics: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.users}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('User metrics API error:', e);
      return 0;
    }
  },

  // Metrics: Sellers count
  getSellerMetrics: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.sellers}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Seller metrics API error:', e);
      return 0;
    }
  },

  // Metrics: Products count
  getProductMetrics: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.products}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Product metrics API error:', e);
      return 0;
    }
  },

  // Metrics: Orders count
  getOrderMetrics: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.orders}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Order metrics API error:', e);
      return 0;
    }
  },

  // Metrics: Sales (by seller/category/community)
  getSalesMetrics: async () => {
    try {
      const url = `${API_ENDPOINTS.metrics.sales}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return result?.data || { salesBySeller: [], salesByCategory: [], salesByCommunity: [] };
    } catch (e) {
      console.error('Sales metrics API error:', e);
      return { salesBySeller: [], salesByCategory: [], salesByCommunity: [] };
    }
  },

  // Metrics: Revenue total count
  getRevenueMetrics: async () => {
    try {
      const url = `${API_ENDPOINTS.metrics.revenue}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result?.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Revenue metrics API error:', e);
      return 0;
    }
  },

  // Metrics: Wallet total (optional sellerId)
  getWalletTotal: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.walletsTotal}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result?.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Wallet total API error:', e);
      return 0;
    }
  },

  // Metrics: Earnings (optional sellerId)
  getEarnings: async (params = {}) => {
    try {
      const search = new URLSearchParams(params);
      const url = `${API_ENDPOINTS.metrics.earnings}${search.toString() ? `?${search.toString()}` : ''}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return typeof result?.data === 'number' ? result.data : 0;
    } catch (e) {
      console.error('Earnings API error:', e);
      return 0;
    }
  },

  // Metrics: Seller products grouped by category & community
  getSellerProductsGrouped: async (sellerId) => {
    if (!sellerId) return null;
    try {
      const url = API_ENDPOINTS.metrics.sellerProductsGrouped(sellerId);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return result?.data || null;
    } catch (e) {
      console.error('Seller products grouped API error:', e);
      return null;
    }
  },

  // Banners
  getBanners: async () => {
    try {
      const url = API_ENDPOINTS.banners;
      const token = localStorage.getItem('access_token');
      const headers = { };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return Array.isArray(result?.data) ? result.data : [];
    } catch (e) {
      console.error('Get Banners API error:', e);
      return [];
    }
  },

  createBanner: async (payload) => {
    try {
      const url = API_ENDPOINTS.banners;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Create Banner API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  updateBanner: async (id, payload) => {
    try {
      const url = API_ENDPOINTS.updateBanner(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Update Banner API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  uploadFiles: async (files) => {
    try {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      const token = localStorage.getItem('access_token');
      const headers = { };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(API_ENDPOINTS.fileUpload, { method: 'POST', headers, body: form });
      const result = await response.json();
      return Array.isArray(result?.data) ? result.data : [];
    } catch (e) {
      console.error('File upload API error:', e);
      return [];
    }
  },

  // Demo: Recent orders
  getRecentOrders: async () => {
    await delay(500);
    return [
      { id: 'ORD001', product: 'Backpack', units: 2, date: '2025-08-08', cost: '$120', status: 'Completed' },
      { id: 'ORD002', product: 'T-Shirt', units: 1, date: '2025-08-08', cost: '$40', status: 'Delayed' },
      { id: 'ORD003', product: 'Shoes', units: 1, date: '2025-08-07', cost: '$80', status: 'On Hold' },
      
    ];
  },

  // Demo: New Customers
  getNewCustomers: async () => {
    await delay(500);
    return [
      { name: 'John Doe', handle: '@johndoe', img: '/assets/img/user/john.png', orders: 3, amount: '$320' },
      { name: 'Jane Smith', handle: '@janesmith', img: '/assets/img/user/jane.png', orders: 2, amount: '$210' },
      
    ];
  },

  // Demo: Top Products
  getTopProducts: async () => {
    await delay(500);
    return [
      { name: 'Backpack', img: '/assets/img/products/backpack.png', sales: 120, desc: 'Durable travel backpack', price: '$120', old: '$150' },
      { name: 'T-Shirt', img: '/assets/img/products/tshirt.png', sales: 90, desc: 'Cotton t-shirt', price: '$40', old: '$50' },
      
    ];
  },

  // Demo: Country Purchases (with top cities)
  getCountryPurchases: async () => {
    await delay(500);
    return {
      Nigeria: 3200,
      Ghana: 2400,
      Kenya: 1800,
      USA: 1200,
      UK: 900,
      Brazil: 3000,
      cities: [
        { name: 'Lagos', value: 210 },
        { name: 'Accra', value: 160 },
        { name: 'Nairobi', value: 130 },
        { name: 'NYC', value: 90 },
        { name: 'London', value: 70 },
      ]
    };
  },

  // Demo: Sold by Items
  getSoldItems: async () => {
    await delay(500);
    return [
      { name: 'Backpack', qty: 9, change: '33%', dir: 'up' },
      { name: 'T-Shirt', qty: 6, change: '150%', dir: 'up' },
      { name: 'Coat', qty: 3, change: '50%', dir: 'up' },
      { name: 'Necklace', qty: 7, change: '150%', dir: 'up' },
      { name: 'Jeans Pant', qty: 10, change: '300%', dir: 'down' },
      { name: 'Shoes', qty: 5, change: '100%', dir: 'up' },
      { name: 'Watches', qty: 18, change: '160%', dir: 'up' },
      { name: 'Inner', qty: 156, change: '120%', dir: 'up' },
    ];
  },

  // Demo: Current Users (for chart)
  getCurrentUsers: async () => {
    await delay(500);
    return {
      active: 120,
      inactive: 30,
      guests: 50,
      
    };
  },

  // Demo: User Activity (for chart)
  getUserActivity: async () => {
    await delay(500);
    return [
      { date: '2025-08-01', activity: 50 },
      { date: '2025-08-02', activity: 60 },
      { date: '2025-08-03', activity: 40 },
      { date: '2025-08-04', activity: 80 },
      { date: '2025-08-05', activity: 90 },
    ];
  },

  // Demo: Sales Report (for chart)
  getSalesReport: async () => {
    await delay(500);
    return {
      today: [120, 90, 60, 80, 100],
      monthly: [3000, 2800, 3200, 3100, 3300],
      yearly: [35000, 34000, 36000, 37000, 38000],
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    };
  },

  // Demo: Orders Overview (for chart)
  getOrdersOverview: async () => {
    await delay(500);
    return {
      completed: 120,
      unpaid: 30,
      returned: 10,
      pending: 15,
      canceled: 5,
      lost: 2,
    };
  },

  // Demo: Orders Listing (for Orders page)
  getOrders: async (page = 1, limit = 10) => {
    try {
      const url = `${API_ENDPOINTS.orders}?page=${page}&limit=${limit}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      const result = await response.json();
      console.log('Orders API raw response:', result);
      const orders = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      const mappedOrders = orders.map(order => ({
        id: order.id,
        customer: order.user?.fullName || order.user?.firstName || 'N/A',
        seller: order.seller?.name || 'N/A',
        product: order.items && order.items.length > 0 ? order.items.map(i => i.quantity + 'x').join(', ') : 'N/A',
        items: order.items ? order.items.reduce((sum, i) => sum + (i.quantity || 0), 0) : 0,
        total: parseFloat(order.total) || 0,
        orderStatus: order.status || order.orderProgressStatus || 'N/A',
        deliveryType: order.address ? 'Door Delivery' : 'Pickup',
        paymentStatus: order.isReviewed ? 'Paid' : 'Pending',
        dateTime: order.createdAt ? new Date(order.createdAt).toLocaleString() : '',
      }));
      console.log('Orders API mapped orders:', mappedOrders);
      return {
        orders: mappedOrders,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || mappedOrders.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      // fallback to demo data
      const demoOrders = [
        { id: 'ORD-1001', customer: 'Ava Smith', seller: 'Aurora Mart', product: 'Winter Jacket', items: 3, total: 149.99, orderStatus: 'Confirmed', deliveryType: 'Door Delivery', paymentStatus: 'Paid', dateTime: '2025-02-01 10:15' },
        { id: 'ORD-1002', customer: 'Johnee Bolbi', seller: 'Bolbi Stores', product: 'Running Shoes', items: 1, total: 59.99, orderStatus: 'Cancelled', deliveryType: 'Pickup', paymentStatus: 'Refunded', dateTime: '2025-02-02 11:05' },
        { id: 'ORD-1003', customer: 'Mohini Marlo', seller: 'Marlo Traders', product: 'Smart Watch', items: 5, total: 329.49, orderStatus: 'Out for Delivery', deliveryType: 'Door Delivery', paymentStatus: 'Paid', dateTime: '2025-02-03 09:40' },
        { id: 'ORD-1004', customer: 'Nitilo Kathilo', seller: 'Kathilo LLC', product: 'Socks', items: 2, total: 89.0, orderStatus: 'Pending', deliveryType: 'Pickup', paymentStatus: 'Pending', dateTime: '2025-02-04 14:10' },
        { id: 'ORD-1005', customer: 'Hardi Sandhu', seller: 'Sandhu Co', product: 'Hoodie', items: 4, total: 210.0, orderStatus: 'Delivered', deliveryType: 'Door Delivery', paymentStatus: 'Paid', dateTime: '2025-02-05 16:22' },
      ];
      return {
        orders: demoOrders,
        page,
        limit,
        total: demoOrders.length,
        noOfPages: 1,
        nextPage: null,
      };
    }
  },

  // Subscribers Listing
  getSubscribers: async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({ page, limit, ...filters });
      const url = `${API_ENDPOINTS.subscribers}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers, method: 'GET' });
      const result = await response.json();
      const list = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      // Normalize to table needs
      const subscribers = list.map(item => ({
        id: item.id,
        email: item.email,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
      return {
        subscribers,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || subscribers.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Get Subscribers API error:', e);
      return { subscribers: [], page, limit, total: 0, noOfPages: 1, nextPage: null };
    }
  },

  // Sellers Listing
  getSellers: async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({ page, limit, ...filters });
      const url = `${API_ENDPOINTS.sellers}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      const list = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      const sellers = list.map(s => ({
        id: s.id,
        name: s.name,
        desc: s.desc,
        email: s.email,
        phone: s.phone,
        status: s.status,
        verificationStatus: s.verificationStatus,
        isClosed: !!s.isClosed,
        imageUrl: s.image?.url || null,
        availability: s.availability || null,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
      }));
      return {
        sellers,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || sellers.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Get Sellers API error:', e);
      return { sellers: [], page, limit, total: 0, noOfPages: 1, nextPage: null };
    }
  },

  // Get seller by ID
  getSellerById: async (id) => {
    try {
      const url = API_ENDPOINTS.getSellerById(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return {
        ok: response.ok,
        data: result.data || null,
        message: result.message || null,
      };
    } catch (e) {
      console.error('Get Seller By ID API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  // Activate seller account (admin)
  activateSeller: async (email, payload) => {
    try {
      const url = API_ENDPOINTS.activateSeller(email);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return {
        ok: response.ok,
        data: result.data || null,
        message: result.message || null,
      };
    } catch (e) {
      console.error('Activate Seller API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  // Update seller status (admin)
  updateSellerStatus: async (sellerId, status) => {
    try {
      const url = API_ENDPOINTS.updateSellerStatus(sellerId);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
      const result = await response.json();
      return {
        ok: response.ok,
        data: result.data || null,
        message: result.message || null,
      };
    } catch (e) {
      console.error('Update Seller Status API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  getProducts: async (page = 1, limit = 10, filters = {}) => {
    try {
      // Build query params
      const params = new URLSearchParams({
        page,
        limit,
        ...filters
      });
      const url = `${API_ENDPOINTS.products}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      const result = await response.json();
      console.log('Products API raw response:', result);
      const products = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      // Map products to a usable format for frontend
      const mappedProducts = products.map(product => ({
        id: product.id,
        name: product.name,
        description: product.description,
        price: product.price,
        discountInPercent: product.discountInPercent,
        quantity: product.quantity,
        unitSold: product.unitSold,
        weight: product.weight,
        images: product.images,
        status: product.status,
        rating: product.rating,
        totalRating: product.totalRating,
        communities: product.communities,
        category: product.category,
        seller: product.seller,
        sellerId: product.sellerId || (product.seller && product.seller.id),
        createdAt: product.createdAt,
        updatedAt: product.updatedAt,
      }));
      return {
        products: mappedProducts,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || mappedProducts.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Products API error:', e);
      return {
        products: [],
        page,
        limit,
        total: 0,
        noOfPages: 1,
        nextPage: null,
      };
    }
  },

  deleteProduct: async (id) => {
    try {
      const url = `${API_ENDPOINTS.products}/${id}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      const result = await response.json();
      return result.data !== undefined;
    } catch (e) {
      console.error('Delete Product API error:', e);
      return false;
    }
  },

  // Create a product
  createProduct: async (product) => {
    try {
      const url = `${API_ENDPOINTS.products}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(product),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Create Product API error:', e);
      return null;
    }
  },

  // Update a product
  updateProduct: async (productId, updates, sellerId) => {
    try {
      const url = API_ENDPOINTS.updateProduct(productId, sellerId);
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Update Product API error:', e);
      return null;
    }
  },

  // Get all categories
  getCategories: async () => {
    try {
      const url = `${API_ENDPOINTS.categories}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      const result = await response.json();
      return Array.isArray(result.data) ? result.data : [];
    } catch (e) {
      console.error('Categories API error:', e);
      return [];
    }
  },

  // Create a category
  createCategory: async (category) => {
    try {
      const url = `${API_ENDPOINTS.categories}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(category),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Create Category API error:', e);
      return null;
    }
  },

  // Update a category
  updateCategory: async (id, updates) => {
    try {
      const url = `${API_ENDPOINTS.categories}/${id}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Update Category API error:', e);
      return null;
    }
  },

  // Delete a category
  deleteCategory: async (id) => {
    try {
      const url = `${API_ENDPOINTS.categories}/${id}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      const result = await response.json();
      return result.data !== undefined;
    } catch (e) {
      console.error('Delete Category API error:', e);
      return false;
    }
  },
  // Get all communities
  getCommunities: async () => {
    try {
      const url = `${API_ENDPOINTS.communities}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      const result = await response.json();
      return Array.isArray(result.data) ? result.data : [];
    } catch (e) {
      console.error('Communities API error:', e);
      return [];
    }
  },

  // Create a community
  createCommunity: async (community) => {
    try {
      const url = `${API_ENDPOINTS.communities}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(community),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Create Community API error:', e);
      return null;
    }
  },

  // Update a community
  updateCommunity: async (id, updates) => {
    try {
      const url = `${API_ENDPOINTS.communities}/${id}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Update Community API error:', e);
      return null;
    }
  },

  // Delete a community
  deleteCommunity: async (id) => {
    try {
      const url = `${API_ENDPOINTS.communities}/${id}`;
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      const result = await response.json();
      return result.data !== undefined;
    } catch (e) {
      console.error('Delete Community API error:', e);
      return false;
    }
  },
  // Support Tickets
  getTickets: async (page = 1, limit = 10, ticketId = null) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (ticketId) params.append('ticketId', ticketId);
      const url = `${API_ENDPOINTS.tickets}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      const tickets = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      return {
        tickets,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || tickets.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Get Tickets API error:', e);
      return { tickets: [], page, limit, total: 0, noOfPages: 1, nextPage: null };
    }
  },

  getTicketById: async (id) => {
    try {
      const url = API_ENDPOINTS.getTicketById(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Get Ticket By ID API error:', e);
      return null;
    }
  },

  updateTicket: async (id, updates) => {
    try {
      const url = API_ENDPOINTS.updateTicket(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Update Ticket API error:', e);
      return null;
    }
  },
  // Transactions
  getTransactions: async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = new URLSearchParams({ page, limit, ...filters });
      const url = `${API_ENDPOINTS.transactions}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      const transactions = (result && result.data && Array.isArray(result.data.data)) ? result.data.data : [];
      return {
        transactions,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || transactions.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Get Transactions API error:', e);
      return { transactions: [], page, limit, total: 0, noOfPages: 1, nextPage: null };
    }
  },

  getTransactionById: async (id) => {
    try {
      const url = API_ENDPOINTS.getTransactionById(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return result.data || null;
    } catch (e) {
      console.error('Get Transaction By ID API error:', e);
      return null;
    }
  },
  // Get customers with pagination
  getCustomers: async (page = 1, limit = 10) => {
    try {
      const url = API_ENDPOINTS.getCustomers(page, limit);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      if (!response.ok) {
        return { users: [], error: result.message || `Error: ${response.status}` };
      }
      const users = Array.isArray(result.data?.data) ? result.data.data : [];
      return {
        users,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || users.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
        error: null
      };
    } catch (e) {
      return { users: [], error: e?.message || 'Failed to fetch customers' };
    }
  },

  // Announcements
  createAnnouncement: async (payload) => {
    try {
      const url = API_ENDPOINTS.announcements;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Create Announcement API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  updateAnnouncement: async (id, payload) => {
    try {
      const url = API_ENDPOINTS.updateAnnouncement(id);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Update Announcement API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  getAnnouncements: async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({ page, limit });
      const url = `${API_ENDPOINTS.announcements}?${params.toString()}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      const announcements = Array.isArray(result.data?.data) ? result.data.data : [];
      return {
        announcements,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || announcements.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
      };
    } catch (e) {
      console.error('Get Announcements API error:', e);
      return { announcements: [], page, limit, total: 0, noOfPages: 1, nextPage: null };
    }
  },
  // Get refunds with pagination
  getRefunds: async (page = 1, limit = 10) => {
    try {
      const url = `${API_ENDPOINTS.refunds}?page=${page}&limit=${limit}`;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      if (!response.ok) {
        return { refunds: [], error: result.message || `Error: ${response.status}` };
      }
      const refunds = Array.isArray(result.data?.data) ? result.data.data : [];
      return {
        refunds,
        page: result.data?.page ? parseInt(result.data.page) : page,
        limit: result.data?.limit ? parseInt(result.data.limit) : limit,
        total: result.data?.total || refunds.length,
        noOfPages: result.data?.noOfPages || 1,
        nextPage: result.data?.nextPage || null,
        error: null
      };
    } catch (e) {
      return { refunds: [], error: e?.message || 'Failed to fetch refunds' };
    }
  },

  // Admin Management
  createAdmin: async (payload) => {
    try {
      const url = API_ENDPOINTS.createAdmin;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Create Admin API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  updateAdmin: async (adminId, payload) => {
    try {
      const url = API_ENDPOINTS.updateAdmin(adminId);
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(payload) });
      const result = await response.json();
      return { ok: response.ok, data: result?.data || null, message: result?.message || null };
    } catch (e) {
      console.error('Update Admin API error:', e);
      return { ok: false, data: null, message: e?.message || 'Request failed' };
    }
  },

  getAdmins: async () => {
    try {
      const url = API_ENDPOINTS.getAdmins;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return Array.isArray(result.data) ? result.data : [];
    } catch (e) {
      console.error('Get Admins API error:', e);
      return [];
    }
  },

  getAdminPermissions: async () => {
    try {
      const url = API_ENDPOINTS.getAdminPermissions;
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      const result = await response.json();
      return Array.isArray(result.data) ? result.data : [];
    } catch (e) {
      console.error('Get Admin Permissions API error:', e);
      return [];
    }
  },
};
